import {
  Component,
  Output,
  Input,
  EventEmitter,
  SimpleChanges,
  Inject,
} from "@angular/core";
import { faArrowToBottom, faChartLine } from "@fortawesome/pro-solid-svg-icons";
import { UserTprSettingObject, VsPeerState } from "../tpr.model";
import { getTickersId, updateColumnPickerData } from "../tpr-utils";
import { NzModalService, NzModalRef } from "ng-zorro-antd/modal";
import * as Highcharts from "highcharts";
import { CompsTickerModalComponent } from "../../comps-page/comps-ticker-modal/comps-ticker-modal.component";
import { TPR_VSPEERS_PRICE_PERFORMANCE_OPTIONS } from "../tpr-constants";
import { Subscription } from "rxjs";
import { getSelectedColumnPickerItemName } from "src/app/utils/common";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { DOCUMENT } from "@angular/common";
import { MatSnackBarRef } from "@angular/material/snack-bar";
import { TPRDataService } from "../tpr-data.service";
import { AppService } from "src/app/app.service";

@Component({
  selector: "app-tpr-peers-filter",
  templateUrl: "./tpr-peers-filter.component.html",
  styleUrls: ["./tpr-peers-filter.component.scss"],
})
export class TprPeersFilterComponent {
  //inputs, outputs
  @Input() primaryTicker: string;
  @Input() currentCompany: number;
  @Input() isMetaReady = false;
  @Input() state: VsPeerState;
  @Input() watchlistData: Array<any> = [];
  @Input() recentlyVisitedCompanies: Array<any> = [];
  @Input() suggestedPeers: Array<any> = [];
  @Input() isTickerFilterDataReady = false;
  @Input() isUserPeriodCalSettingsInProgress = false;
  @Input() apiEP: string = window.apiUrl;
  @Input() columnPickerSelectedColumns: Array<any> = [];
  @Input() columnPickerAvailableColumns: Array<any> = [];
  @Input() columnPickerData: Array<any> = [];
  @Input() tprVsPeersColumnPickerItem: Array<any> = [];
  @Input() isUserSettingsInProgress: boolean;
  @Input() isWatchListAndRecentCompDataReady: boolean;
  @Input() isTprColumnDetailsSaveApiInProgress: boolean;

  @Output() onPeersChange: EventEmitter<any> = new EventEmitter();
  @Output() showSnackBarMessage: EventEmitter<any> = new EventEmitter();
  @Output() onCurrencyChange = new EventEmitter();
  @Output() updatePricePerformance = new EventEmitter();
  @Output() onColumnPickerChange = new EventEmitter();
  @Inject(DOCUMENT) private _document: HTMLDocument;

  //variables
  orientation = "grid";
  currentTabId = "peer";
  selectedLineItemCount = 0;
  selectedTickerIds = [];
  originallySelectedColumnsPickerData = [];
  gridColumnsData = [];
  isVisibleEditTickerModal = false;
  isVisibleEditLiModal = false;
  pricePerformanceAvailable = false;
  gridAverageData;
  gridMedianData;
  pricePerformanceOptions = [...TPR_VSPEERS_PRICE_PERFORMANCE_OPTIONS];

  //excel export
  excelToExportDropdownOptions = [
    {
      label: "Values",
      id: 1,
      disabled: false,
    },
    {
      label: "Excel Add-In Formulas",
      id: 0,
      disabled: false,
    },
  ];
  isExportPopoverVisible = false;
  isExportInProgress = false;
  //snackbar
  snackBarRef: MatSnackBarRef<any>;
  snackbarMessage = "";

  snackbarInfo: { message: string; type: string } = {
    message: "",
    type: "info",
  };

  userTprSettingDetails: UserTprSettingObject;
  tickersModal: NzModalRef;
  chartInstance: Highcharts.Chart;

  //Subscription
  companyBrokerDetailsSubscriber: Subscription;
  saveTprColumnDetailSubscriber: Subscription;

  //icons
  faArrowToBottom = faArrowToBottom;
  faChartLine = faChartLine;

  //loaders
  isColumnPickerModalVisible = false;

  constructor(
    private modalService: NzModalService,
    private dataService: TPRDataService,
    private appService: AppService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // Set userSetting obj on meta completion
    if (
      this.isMetaReady &&
      changes.columnPickerSelectedColumns &&
      changes.columnPickerSelectedColumns.currentValue !==
        changes.columnPickerSelectedColumns.previousValue
    ) {
      this.settingColumnPickerData(this.columnPickerSelectedColumns);
    }

    if (
      this.isMetaReady &&
      changes.state &&
      changes.state.currentValue &&
      changes.state.previousValue &&
      changes.state.currentValue.ticker_info !==
        changes.state.previousValue.ticker_info
    ) {
      this.selectedTickerIds = getTickersId(
        changes.state.currentValue.ticker_info,
      );
    }
  }

  // Peer Select Tab
  /**
   * Executes on click of Edit ticker button of ticker filter.
   * It creates Edit ticker modal with required data and opens it
   */
  handleEditTickerClick() {
    const tickerInfo =
      this.state && this.state.ticker_info ? [...this.state.ticker_info] : [];
    this.tickersModal = this.modalService.create({
      nzTitle: "Edit Tickers",
      nzContent: CompsTickerModalComponent,
      nzMaskClosable: false,
      nzComponentParams: {
        addRowTableData: tickerInfo,
        watchlists: this.watchlistData,
        recentlyVisitedCompanies: this.recentlyVisitedCompanies,
        maxHeight: "340px",
        type: "edit",
        mptApplicable: false,
        maxTickers: 50,
      },
      nzWrapClassName: "comps-modal-container vertical-center-modal",
      // nzWrapClass"name": "watchlist-modal-container search-and-add-modal-container",
      nzFooter: [
        {
          label: "Cancel",
          type: "default",
          onClick: (): void => {
            this.tickersModal.destroy();
          },
        },
        {
          label: "Update",
          type: "primary",
          class: "update-btn",
          onClick: (data): void => {
            this.updateTickers(data.modalData);
          },
          disabled: (data) => {
            return data.modalData.length > 50 || data.modalData.length === 0;
          },
        },
      ],
    });
  }

  /**
   * It updates the peer companies data.
   * It posts the updated peer to userSetting api
   * It emits onPeersChange which is useful for updating the state object
   * @param {array} ticker - array of all selected tickers
   * @param {object} addedTicker - current selected ticker
   */
  updateTickers(tickers, addedTicker?: { ticker: string; [x: string]: any }) {
    const updatedTickers = tickers.map((item) => ({
      ...item,
      tickerDeleteDisabled: item.ticker === this.primaryTicker,
    }));
    this.onPeersChange.emit({ updatedTickers, addedTicker });
    if (this.tickersModal) {
      this.destroyTickerModal();
    }
  }

  /**
   * It destroys Edit ticker modal
   */
  destroyTickerModal(): void {
    this.tickersModal.destroy();
  }

  /**
   * It executes when we select a ticker from inline search ticker filter
   * It checks if the ticker is already present or not. If ticker is present it shows Info alert if not it add the ticker to the list
   * It emits onPeersChange which is useful for updating the state object
   * @param {object} tickerData - selected ticker object
   */
  handleSearchTickerSelection(tickerData) {
    const { value } = tickerData;
    if (this.selectedTickerIds.includes(value.ticker)) {
      this.snackbarInfo.message = "This ticker is already present.";
      this.snackbarInfo.type = "info";
      this.showSnackBarMessage.emit(this.snackbarInfo);
    } else {
      value["cid"] = value.companyid || value.cid;
      value["name"] = value.n || value.name || value.full_name;
      this.updateTickers([...this.state.ticker_info, value], value);
    }
  }

  //Currency Select Tab
  updateCurrency(item: string) {
    this.onCurrencyChange.emit(item);
  }

  // Column Picker
  hanldeColumnPickerClick() {
    this.isColumnPickerModalVisible = !this.isColumnPickerModalVisible;
  }

  handleColumnPickerResetClick() {
    this.columnPickerData = [...this.tprVsPeersColumnPickerItem];
  }

  handleColumnPickerCancelClick() {
    this.columnPickerData = [...this.originallySelectedColumnsPickerData];
    this.isColumnPickerModalVisible = false;
  }

  handleColumnDrag(data) {
    const selectedItems = data.componentState.selectedItems;
    // There are four fixed column, so currentIndex of dragged items should be more than 3
    if (data.event.currentIndex > 3) {
      moveItemInArray(
        selectedItems,
        data.event.previousIndex,
        data.event.currentIndex,
      );
      this.columnPickerData = this.columnPickerData.map((column) => {
        const index = selectedItems.findIndex(
          (sColumn) => sColumn.id === column.id,
        );
        column.index = index;
        // This component is now aware if some new column was selected/deselected in the modal
        // it would have come to know only on click of apply button. But, now it has to feed in
        // new array of columns, so it should update the selection as well.
        column.isSelected = index !== -1;
        return column;
      });
    }
  }

  settingColumnPickerData(selectedArr) {
    const selectedGridColumns = selectedArr || this.tprVsPeersColumnPickerItem;
    this.pricePerformanceAvailable = selectedArr.includes("Price Performance");
    const columnPickerData = updateColumnPickerData(
      this.columnPickerData,
      selectedGridColumns,
    );
    this.columnPickerData = [...columnPickerData];
    this.originallySelectedColumnsPickerData = [...columnPickerData];
  }

  hanldeColumnPickerApplyClick(value) {
    const selectedItems =
      value && value.selectedItems ? value.selectedItems : [];

    const selectedColumns = getSelectedColumnPickerItemName(selectedItems);
    this.onColumnPickerChange.emit(selectedColumns);
    this.handleColumnPickerCancelClick();
  }

  onPricePerformanceChange(optionClicked) {
    this.updatePricePerformance.emit(optionClicked);
  }

  handleETEListItemClick(exportType) {
    this.isExportInProgress = true;
    const templateType = "tpr vs peers";
    const params = `export_type=${exportType}&template_type=${templateType}&ticker=${this.primaryTicker}&tab=peer`;
    this.dataService.exportTprCompany(params).subscribe(
      (res) => {
        if (res["status"] === "success") {
          const fileName = res["filename"];
          const trackerId = res["trackerid"];
          this.appService.logUserAction(
            {},
            "",
            "",
            "TPR_VS_PEERS_EXCEL_EXPORT",
            {
              templateType: templateType,
              export_type: exportType,
              tracker_id: trackerId,
              file_name: fileName,
            },
            "",
            "TPR VS PEERS EXPORT",
          );
          this.isExportInProgress = false;
        } else {
          this.isExportInProgress = false;
          this.snackbarInfo.message =
            "TPR vs Peers export couldn't be downloaded.";
          this.snackbarInfo.type = "failure";
        }
        this.isExportPopoverVisible = false;
      },
      (err) => {
        this.isExportInProgress = false;
        console.error(err);
      },
    );
  }
}
